import { forwardRef, useId, useState } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { space, text } from '../../../../styles/onboardingUiTokens';
import { SvgIcon } from '../icons/SvgIcon';
import {
  DEFAULT_FORM_HINT_PLACEMENT,
  FormFieldError,
  FormFieldHintMaybe,
  FormFieldRoot,
  FormTextControl,
  type FormHintPlacement,
} from './formFieldLayout';
import { Label } from './Label';

const InputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const Trailing = styled.div`
  position: absolute;
  right: ${space.xs};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 1;
`;

const TextAction = styled.button`
  appearance: none;
  margin: 0;
  padding: ${space.xxs} ${space.xs};
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.medium};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${text.caption.fontSize};
  line-height: ${text.caption.lineHeight};
  font-weight: 600;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${colors.dark};
    background: ${getMediumRGBA(0.08)};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const IconAction = styled(TextAction)`
  line-height: 0;
  padding: ${space.xxs};
`;

const CompactFieldLabel = styled.label`
  margin: 0;
  font-size: ${text.mini.fontSize};
  line-height: ${text.mini.lineHeight};
  font-weight: ${text.mini.fontWeight};
  color: ${getMediumRGBA(0.85)};
  width: 100%;
`;

type Omitted = 'onChange' | 'value' | 'size' | 'children';

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, Omitted> & {
  value: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  /** Visual required indicator on the label; set native `required` on the input separately when applicable. */
  labelRequired?: boolean;
  /** Shows the optional pill next to the label. */
  labelOptional?: boolean;
  /** Copy for the optional pill (default: "Optional"). */
  optionalLabel?: string;
  /** `compact` uses a smaller, muted caption-style `<label>` (no required/optional adornments — use for grouped fields). */
  labelVariant?: 'default' | 'compact';
  hint?: string;
  /** Where `hint` is shown relative to the control. Default: `below`. */
  hintPlacement?: FormHintPlacement;
  error?: string;
  /** When true, input is `aria-invalid` even if `error` is empty (e.g. grouped field with a shared error elsewhere). */
  ariaInvalid?: boolean;
  showClearButton?: boolean;
  /** When `type === 'password'`, show a visibility toggle (does nothing if type is not password). */
  passwordToggle?: boolean;
  clearButtonAriaLabel?: string;
  passwordShowAriaLabel?: string;
  passwordHideAriaLabel?: string;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, ref) {
  const {
    value,
    onChange,
    label,
    labelRequired = false,
    labelOptional = false,
    optionalLabel,
    labelVariant = 'default',
    hint,
    hintPlacement = DEFAULT_FORM_HINT_PLACEMENT,
    error,
    ariaInvalid,
    disabled,
    readOnly,
    showClearButton = false,
    passwordToggle = false,
    clearButtonAriaLabel = 'Eingabe löschen',
    passwordShowAriaLabel = 'Passwort anzeigen',
    passwordHideAriaLabel = 'Passwort verbergen',
    id: idProp,
    type = 'text',
    className,
    'aria-describedby': ariaDescribedByProp,
    ...rest
  } = props;

  const reactId = useId();
  const inputId = idProp ?? `${reactId}-control`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;

  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = type === 'password';
  const resolvedType = isPassword && passwordToggle && passwordVisible ? 'text' : type;

  const showClear = Boolean(showClearButton && value.length > 0 && !disabled && !readOnly);
  const showPwToggle = Boolean(isPassword && passwordToggle && !disabled && !readOnly);

  const trailingExtraPx = (showClear ? 40 : 0) + (showPwToggle ? 88 : 0);
  const trailingPadding = trailingExtraPx > 0 ? `calc(${space.md} + ${trailingExtraPx}px)` : undefined;

  const inputInvalid = Boolean(error?.trim()) || Boolean(ariaInvalid);

  const describedByParts: string[] = [];
  if (hint?.trim()) {
    describedByParts.push(hintId);
  }
  if (error?.trim()) {
    describedByParts.push(errorId);
  }
  if (ariaDescribedByProp) {
    describedByParts.push(ariaDescribedByProp);
  }
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

  const labelNode =
    label != null && label !== '' ? (
      labelVariant === 'compact' ? (
        <CompactFieldLabel htmlFor={inputId}>{label}</CompactFieldLabel>
      ) : (
        <Label htmlFor={inputId} required={labelRequired} optional={labelOptional} optionalText={optionalLabel}>
          {label}
        </Label>
      )
    ) : null;

  return (
    <FormFieldRoot className={className}>
      {labelNode}
      <FormFieldHintMaybe id={hintId} hint={hint} placement={hintPlacement} slot="above" />
      <InputWrap>
        <FormTextControl
          {...rest}
          id={inputId}
          ref={ref}
          $trailingPadding={trailingPadding}
          type={resolvedType}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={inputInvalid}
          aria-describedby={ariaDescribedBy}
          onChange={(e) => onChange?.(e.target.value)}
        />
        {showClear || showPwToggle ? (
          <Trailing>
            {showClear ? (
              <IconAction
                type="button"
                aria-label={clearButtonAriaLabel}
                disabled={disabled || readOnly}
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChange?.('')}
              >
                <SvgIcon name="close" size={20} primaryColor={colors.medium} />
              </IconAction>
            ) : null}
            {showPwToggle ? (
              <TextAction
                type="button"
                aria-pressed={passwordVisible}
                aria-label={passwordVisible ? passwordHideAriaLabel : passwordShowAriaLabel}
                disabled={disabled || readOnly}
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setPasswordVisible((v) => !v)}
              >
                <span aria-hidden>{passwordVisible ? 'Verbergen' : 'Anzeigen'}</span>
              </TextAction>
            ) : null}
          </Trailing>
        ) : null}
      </InputWrap>
      <FormFieldHintMaybe id={hintId} hint={hint} placement={hintPlacement} slot="below" />
      <FormFieldError id={errorId} message={error} />
    </FormFieldRoot>
  );
});

TextInput.displayName = 'TextInput';
